import React, { useState, useEffect, useRef } from 'react';
import { doc, addDoc, updateDoc, collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../firebase';
import { MessageSquare } from 'lucide-react';

const FarmRecordsForm = ({ recordData, onClose, onSuccess }) => {
  const [farmers, setFarmers] = useState([]);
  const [flops, setFlops] = useState([]);
  const [formData, setFormData] = useState({
    farmerId: '',
    farmerName: '',
    flopId: '',
    flopName: '',
    date: '',
    tray: '',
    deliveredTray: '',
    givenAndCrackTray: '',
    newBag: '',
    perDayBag: '',
    givenBag: '',
    mortality: '',
    medicine: '',
    vaccine: '',
    // Field-specific comments
    fieldComments: {
      tray: '',
      deliveredTray: '',
      givenAndCrackTray: '',
      newBag: '',
      perDayBag: '',
      givenBag: '',
      mortality: '',
      medicine: '',
      vaccine: ''
    },
    // Auto calculated fields
    trayStock: '',
    bagStock: '',
    productionPercentage: '',
    feedConsumption: '',
    liveBird: '',
    birdDays: ''
  });
  const [loading, setLoading] = useState(false);
  const [lastRecord, setLastRecord] = useState(null);
  const [selectedFarmerData, setSelectedFarmerData] = useState(null);
  const [commentModal, setCommentModal] = useState({ show: false, field: '', comment: '' });
  const isCalculatingRef = useRef(false);

  // Fetch farmers and flops on component mount
  useEffect(() => {
    fetchFarmers();
    fetchFlops();
  }, []);

  // Handle recordData changes (editing mode)
  useEffect(() => {
    if (recordData) {
      // Editing mode - set all data including calculated fields
      // Only set formData if it hasn't been set yet (to prevent overwriting field comments)
      setFormData(prev => {
        // If formData is already populated and we have the same record, don't overwrite
        if (prev.farmerId === recordData.farmerId && prev.flopId === recordData.flopId && prev.date === recordData.date) {
          console.log('FormData already set, preserving field comments');
          return prev;
        }
        
        // Ensure fieldComments object exists and merge with existing comments
        const existingComments = recordData.fieldComments || {};
        const formDataWithComments = {
          ...recordData,
          fieldComments: {
            tray: existingComments.tray || '',
            deliveredTray: existingComments.deliveredTray || '',
            givenAndCrackTray: existingComments.givenAndCrackTray || '',
            newBag: existingComments.newBag || '',
            perDayBag: existingComments.perDayBag || '',
            givenBag: existingComments.givenBag || '',
            mortality: existingComments.mortality || '',
            medicine: existingComments.medicine || '',
            vaccine: existingComments.vaccine || ''
          }
        };
        console.log('Setting formData for editing with comments:', formDataWithComments.fieldComments);
        return formDataWithComments;
      });
    } else {
      // Adding new record - only set date if it's not already set
      setFormData(prev => ({
        ...prev,
        date: prev.date || new Date().toISOString().split('T')[0]
      }));
    }
  }, [recordData]);

  // Handle selectedFarmerData updates when farmers array is available
  useEffect(() => {
    if (recordData && farmers.length > 0) {
      const selectedFarmer = farmers.find(f => f.id === recordData.farmerId);
      setSelectedFarmerData(selectedFarmer);
    }
  }, [recordData, farmers]);

  useEffect(() => {
    if (formData.farmerId && formData.flopId) {
      fetchLastRecord(formData.farmerId, formData.flopId);
    } else {
      setLastRecord(null);
    }
  }, [formData.farmerId, formData.flopId]);

  useEffect(() => {
    // Recalculate fields when lastRecord or selectedFarmerData changes
    // Only recalculate if we have the necessary data and we're not in editing mode
    if ((lastRecord !== null || selectedFarmerData !== null) && !recordData) {
      calculateFields();
    }
  }, [lastRecord, selectedFarmerData, recordData]);

  // Set date to last record date + 1 day when we get the last record
  useEffect(() => {
    if (lastRecord && !recordData) {
      // Get the last record date and add 1 day
      const lastRecordDate = lastRecord.date?.toDate?.() || new Date(lastRecord.date);
      const nextDate = new Date(lastRecordDate);
      nextDate.setDate(nextDate.getDate() + 1);
      
      // Format as YYYY-MM-DD
      const formattedDate = nextDate.toISOString().split('T')[0];
      
      setFormData(prev => ({
        ...prev,
        date: formattedDate
      }));
    }
  }, [lastRecord, recordData]);

  // Function to update farmer's live birds count
  const updateFarmerLiveBirds = async (farmerId, mortalityChange) => {
    try {
      const farmerRef = doc(db, 'farmers', farmerId);
      
      // Get current farmer data from local state first, then update
      const currentFarmer = farmers.find(f => f.id === farmerId);
      if (currentFarmer) {
        const currentLiveBirds = parseFloat(currentFarmer.liveBirds) || 0;
        const newLiveBirds = Math.max(0, currentLiveBirds - mortalityChange); // Ensure it doesn't go below 0
        
        await updateDoc(farmerRef, {
          liveBirds: newLiveBirds,
          updatedAt: new Date()
        });
        
        console.log(`Updated farmer ${farmerId} live birds from ${currentLiveBirds} to ${newLiveBirds} (mortality change: ${mortalityChange})`);
        
        // Update local state
        setFarmers(prev => prev.map(farmer => 
          farmer.id === farmerId 
            ? { ...farmer, liveBirds: newLiveBirds }
            : farmer
        ));
        
        // Update selectedFarmerData if it's the same farmer
        if (selectedFarmerData && selectedFarmerData.id === farmerId) {
          setSelectedFarmerData(prev => ({ ...prev, liveBirds: newLiveBirds }));
        }
      }
    } catch (error) {
      console.error('Error updating farmer live birds:', error);
      throw error;
    }
  };

  const fetchFarmers = async () => {
    try {
      const q = query(collection(db, 'farmers'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      setFarmers(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching farmers:', error);
    }
  };
  
  const fetchFlops = async () => {
    try {
      const q = query(collection(db, 'flops'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      setFlops(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching flops:', error);
    }
  };

  const fetchLastRecord = async (farmerId, flopId) => {
    try {
      // Temporary workaround while index is building
      // Fetch all records for this farmer and flop, then sort in JavaScript
      const q = query(
        collection(db, 'farmRecords'),
        where('farmerId', '==', farmerId),
        where('flopId', '==', flopId)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        // Sort by date descending and get the first one
        const sortedDocs = querySnapshot.docs.sort((a, b) => {
          const dateA = a.data().date?.toDate?.() || new Date(a.data().date);
          const dateB = b.data().date?.toDate?.() || new Date(b.data().date);
          return dateB - dateA; // Descending order
        });
        const lastRecordData = sortedDocs[0].data();
        console.log('lastRecordData', lastRecordData);
        setLastRecord(lastRecordData);
      } else {
        setLastRecord(null);
      }
    } catch (error) {
      console.error('Error fetching last record:', error);
      setLastRecord(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Trigger calculations when relevant fields change
    if (['tray', 'deliveredTray', 'givenAndCrackTray', 'newBag', 'perDayBag', 'givenBag', 'mortality', 'date'].includes(name)) {
      calculateFields(name, value);
    }
    
    if (name === 'farmerId') {
      const selectedFarmer = farmers.find(f => f.id === value);
      setSelectedFarmerData(selectedFarmer);
      setFormData(prev => ({
        ...prev,
        farmerId: value,
        farmerName: selectedFarmer ? selectedFarmer.name : ''
      }));
    }
    if (name === 'flopId') {
      const selectedFlop = flops.find(f => f.id === value);
      setFormData(prev => ({
        ...prev,
        flopId: value,
        flopName: selectedFlop ? selectedFlop.name : ''
      }));
    }
  };

  const handleFieldCommentChange = (fieldName, comment) => {
    console.log('Setting field comment:', fieldName, comment);
    setFormData(prev => {
      const updatedData = {
        ...prev,
        fieldComments: {
          ...prev.fieldComments,
          [fieldName]: comment
        }
      };
      console.log('Updated formData fieldComments:', updatedData.fieldComments);
      return updatedData;
    });
  };

  const openCommentModal = (fieldName) => {
    const currentComment = formData.fieldComments?.[fieldName] || '';
    console.log('Opening comment modal for:', fieldName, 'Current comment:', currentComment);
    setCommentModal({
      show: true,
      field: fieldName,
      comment: currentComment
    });
  };

  const closeCommentModal = () => {
    setCommentModal({ show: false, field: '', comment: '' });
  };

  const saveFieldComment = () => {
    console.log('Saving field comment:', commentModal.field, commentModal.comment);
    handleFieldCommentChange(commentModal.field, commentModal.comment);
    closeCommentModal();
  };

  const calculateFields = (changedField, changedValue) => {
    // Prevent infinite loops
    if (isCalculatingRef.current) {
      return;
    }
    isCalculatingRef.current = true;
    
    const currentData = { ...formData };
    if (changedField && changedValue !== undefined) {
      currentData[changedField] = changedValue;
    }

    // Get values from form
    const tray = parseFloat(currentData.tray) || 0;
    const deliveredTray = parseFloat(currentData.deliveredTray) || 0;
    const givenAndCrackTray = parseFloat(currentData.givenAndCrackTray) || 0;
    const newBag = parseFloat(currentData.newBag) || 0;
    const perDayBag = parseFloat(currentData.perDayBag) || 0;
    const givenBag = parseFloat(currentData.givenBag) || 0;
    const mortality = parseFloat(currentData.mortality) || 0;

    // Check if we're in editing mode
    const isEditing = !!recordData;

    // Get reference values based on mode
    let lastTrayStock, lastBagStock, lastBirdDays;
    if (isEditing) {
      // In editing mode, use the current record's values as base
      lastTrayStock = parseFloat(currentData.trayStock) || 0;
      lastBagStock = parseFloat(currentData.bagStock) || 0;
      lastBirdDays = parseFloat(currentData.birdDays) || 0;
    } else {
      // In adding mode, use the last record's values
      lastTrayStock = lastRecord ? parseFloat(lastRecord.trayStock) || 0 : 0;
      lastBagStock = lastRecord ? parseFloat(lastRecord.bagStock) || 0 : 0;
      lastBirdDays = lastRecord ? parseFloat(lastRecord.birdDays) || 0 : 0;
    }
    console.log('lastRecord', lastRecord);
    // Get farmer's live birds (default to 0 if no farmer data)
    const farmerLiveBirds = selectedFarmerData ? parseFloat(selectedFarmerData.liveBirds) || 0 : 0;

    // Calculate Tray Stock = last Tray + tray - Delivered Tray - Given and Crack Tray
    let trayStock;
    if (isEditing) {
      // In editing mode, recalculate based on the difference from original values
      const originalTray = parseFloat(recordData.tray) || 0;
      const originalDeliveredTray = parseFloat(recordData.deliveredTray) || 0;
      const originalGivenAndCrackTray = parseFloat(recordData.givenAndCrackTray) || 0;
      
      const trayDiff = tray - originalTray;
      const deliveredDiff = deliveredTray - originalDeliveredTray;
      const givenAndCrackDiff = givenAndCrackTray - originalGivenAndCrackTray;
      
      trayStock = lastTrayStock + trayDiff - deliveredDiff - givenAndCrackDiff;
    } else {
      // In adding mode, use normal calculation
      trayStock = lastTrayStock + tray - deliveredTray - givenAndCrackTray;
    }
    currentData.trayStock = trayStock.toFixed(2);

    // Calculate Bag Stock = last bag stock + new bag - per day bag - given bag
    let bagStock;
    if (isEditing) {
      // In editing mode, recalculate based on the difference from original values
      const originalNewBag = parseFloat(recordData.newBag) || 0;
      const originalPerDayBag = parseFloat(recordData.perDayBag) || 0;
      const originalGivenBag = parseFloat(recordData.givenBag) || 0;
      
      const newBagDiff = newBag - originalNewBag;
      const perDayBagDiff = perDayBag - originalPerDayBag;
      const givenBagDiff = givenBag - originalGivenBag;
      
      bagStock = lastBagStock + newBagDiff - perDayBagDiff - givenBagDiff;
    } else {
      // In adding mode, use normal calculation
      bagStock = lastBagStock + newBag - perDayBag - givenBag;
    }
    currentData.bagStock = bagStock.toFixed(2);

    // Calculate Live Bird = farmer's live birds - mortality
    const liveBird = farmerLiveBirds - mortality;
    currentData.liveBird = liveBird.toFixed(0);

    // Calculate Production Percentage = (tray * 30) * 100 / live bird
    const productionPercentage = liveBird > 0 ? ((tray * 30) * 100 / liveBird) : 0;
    currentData.productionPercentage = productionPercentage.toFixed(2);

    // Calculate Feed Consumption = (per day bag * 50) * 1000 / live bird
    const feedConsumption = liveBird > 0 ? ((perDayBag * 50) * 1000 / liveBird) : 0;
    currentData.feedConsumption = feedConsumption.toFixed(2);

    // Calculate Bird Days based on bird date or last record
    let birdDays;
    let birdAge = '';
    if (selectedFarmerData && selectedFarmerData.birdDate) {
      // Calculate days from bird date to selected record date
      const birdDate = new Date(selectedFarmerData.birdDate);
      const recordDate = new Date(currentData.date);
      const timeDiff = recordDate.getTime() - birdDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      birdDays = Math.max(1, daysDiff); // Minimum 1 day
      birdAge = `${daysDiff} days`;
    } else {
      // Fallback to last record + 1
      birdDays = lastBirdDays + 1;
    }
    currentData.birdDays = birdDays.toFixed(0);
    currentData.birdAge = birdAge;

    setFormData(currentData);
    isCalculatingRef.current = false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      console.log('Saving record with fieldComments:', formData.fieldComments);
      
      const currentMortality = parseFloat(formData.mortality) || 0;
      let mortalityChange = 0;
      
      // Calculate mortality change for farmer update
      if (recordData) {
        // Editing mode - calculate the difference
        const originalMortality = parseFloat(recordData.mortality) || 0;
        mortalityChange = currentMortality - originalMortality;
      } else {
        // Adding new record - use the full mortality value
        mortalityChange = currentMortality;
      }
      
      // Validate that mortality doesn't exceed current live birds
      if (mortalityChange > 0 && selectedFarmerData) {
        const currentLiveBirds = parseFloat(selectedFarmerData.liveBirds) || 0;
        if (mortalityChange > currentLiveBirds) {
          alert(`Mortality (${mortalityChange}) cannot exceed current live birds (${currentLiveBirds}). Please check the values.`);
          setLoading(false);
          return;
        }
      }
      
      const farmRecordData = {
        ...formData,
        farmerId: formData.farmerId,
        farmerName: formData.farmerName,
        flopId: formData.flopId,
        flopName: formData.flopName,
        date: formData.date,
        tray: parseFloat(formData.tray) || 0,
        deliveredTray: parseFloat(formData.deliveredTray) || 0,
        givenAndCrackTray: parseFloat(formData.givenAndCrackTray) || 0,
        newBag: parseFloat(formData.newBag) || 0,
        perDayBag: parseFloat(formData.perDayBag) || 0,
        givenBag: parseFloat(formData.givenBag) || 0,
        mortality: currentMortality,
        medicine: formData.medicine,
        vaccine: formData.vaccine,
        fieldComments: formData.fieldComments,
        trayStock: parseFloat(formData.trayStock) || 0,
        bagStock: parseFloat(formData.bagStock) || 0,
        productionPercentage: parseFloat(formData.productionPercentage) || 0,
        feedConsumption: parseFloat(formData.feedConsumption) || 0,
        liveBird: parseFloat(formData.liveBird) || 0,
        birdDays: parseFloat(formData.birdDays) || 0,
        birdAge: formData.birdAge || '',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Save the farm record first
      if (farmRecordData.id) {
        const docRef = doc(db, 'farmRecords', farmRecordData.id);
        await updateDoc(docRef, farmRecordData);
      } else {
        await addDoc(collection(db, 'farmRecords'), farmRecordData);
      }
      
      // Update farmer's live birds count if there's a mortality change
      if (mortalityChange > 0 && formData.farmerId) {
        await updateFarmerLiveBirds(formData.farmerId, mortalityChange);
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving farm record:', error);
      alert('Error saving data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {recordData ? 'Edit Farm Record' : 'Add New Farm Record'}
            </h2>
            {recordData && (
              <p className="text-sm text-gray-600 mt-1">
                Editing record from {new Date(recordData.date).toLocaleDateString()}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Flop *
              </label>
              <select
                name="flopId"
                value={formData.flopId}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Flop</option>
                {flops.map((flop) => (
                  <option key={flop.id} value={flop.id}>{flop.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Farmer *
              </label>
              <select
                name="farmerId"
                value={formData.farmerId}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Farmer</option>
                {farmers.map((farmer) => (
                  <option key={farmer.id} value={farmer.id}>{farmer.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Manual Entry Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Tray *
                </label>
                <button
                  type="button"
                  onClick={() => openCommentModal('tray')}
                  className={`${formData.fieldComments.tray ? 'text-orange-600' : 'text-gray-400'} hover:text-orange-900`}
                  title={formData.fieldComments.tray || "Add comment for Tray"}
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
              </div>
              <input
                type="number"
                name="tray"
                value={formData.tray}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Delivered Tray *
                </label>
                <button
                  type="button"
                  onClick={() => openCommentModal('deliveredTray')}
                  className={`${formData.fieldComments.deliveredTray ? 'text-orange-600' : 'text-gray-400'} hover:text-orange-900`}
                  title={formData.fieldComments.deliveredTray || "Add comment for Delivered Tray"}
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
              </div>
              <input
                type="number"
                name="deliveredTray"
                value={formData.deliveredTray}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Given & Crack Tray *
                </label>
                <button
                  type="button"
                  onClick={() => openCommentModal('givenAndCrackTray')}
                  className={`${formData.fieldComments.givenAndCrackTray ? 'text-orange-600' : 'text-gray-400'} hover:text-orange-900`}
                  title={formData.fieldComments.givenAndCrackTray || "Add comment for Given & Crack Tray"}
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
              </div>
              <input
                type="number"
                name="givenAndCrackTray"
                value={formData.givenAndCrackTray}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  New Bag *
                </label>
                <button
                  type="button"
                  onClick={() => openCommentModal('newBag')}
                  className={`${formData.fieldComments.newBag ? 'text-orange-600' : 'text-gray-400'} hover:text-orange-900`}
                  title={formData.fieldComments.newBag || "Add comment for New Bag"}
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
              </div>
              <input
                type="number"
                name="newBag"
                value={formData.newBag}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Per Day Bag *
                </label>
                <button
                  type="button"
                  onClick={() => openCommentModal('perDayBag')}
                  className={`${formData.fieldComments.perDayBag ? 'text-orange-600' : 'text-gray-400'} hover:text-orange-900`}
                  title={formData.fieldComments.perDayBag || "Add comment for Per Day Bag"}
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
              </div>
              <input
                type="number"
                name="perDayBag"
                value={formData.perDayBag}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Given Bag *
                </label>
                <button
                  type="button"
                  onClick={() => openCommentModal('givenBag')}
                  className={`${formData.fieldComments.givenBag ? 'text-orange-600' : 'text-gray-400'} hover:text-orange-900`}
                  title={formData.fieldComments.givenBag || "Add comment for Given Bag"}
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
              </div>
              <input
                type="number"
                name="givenBag"
                value={formData.givenBag}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Mortality *
                </label>
                <button
                  type="button"
                  onClick={() => openCommentModal('mortality')}
                  className={`${formData.fieldComments.mortality ? 'text-orange-600' : 'text-gray-400'} hover:text-orange-900`}
                  title={formData.fieldComments.mortality || "Add comment for Mortality"}
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
              </div>
              <input
                type="number"
                name="mortality"
                value={formData.mortality}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-blue-600 mt-1">
                ⚠️ This will automatically reduce the farmer's live birds count
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Medicine
                </label>
                <button
                  type="button"
                  onClick={() => openCommentModal('medicine')}
                  className={`${formData.fieldComments.medicine ? 'text-orange-600' : 'text-gray-400'} hover:text-orange-900`}
                  title={formData.fieldComments.medicine || "Add comment for Medicine"}
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
              </div>
              <input
                type="text"
                name="medicine"
                value={formData.medicine}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Medicine details"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Vaccine
                </label>
                <button
                  type="button"
                  onClick={() => openCommentModal('vaccine')}
                  className={`${formData.fieldComments.vaccine ? 'text-orange-600' : 'text-gray-400'} hover:text-orange-900`}
                  title={formData.fieldComments.vaccine || "Add comment for Vaccine"}
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
              </div>
              <input
                type="text"
                name="vaccine"
                value={formData.vaccine}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Vaccine details"
              />
            </div>


          </div>

          {/* Auto Calculated Fields */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Auto Calculated Fields</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tray Stock (Auto)
                </label>
                <input
                  type="number"
                  name="trayStock"
                  value={formData.trayStock}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">Last Tray + Tray - Delivered Tray - Given & Crack Tray</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bag Stock (Auto)
                </label>
                <input
                  type="number"
                  name="bagStock"
                  value={formData.bagStock}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">Last Bag Stock + New Bag - Per Day Bag - Given Bag</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Production Percentage (Auto)
                </label>
                <input
                  type="number"
                  name="productionPercentage"
                  value={formData.productionPercentage}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">(Tray × 30) × 100 / Live Bird</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Feed Consumption (Auto)
                </label>
                <input
                  type="number"
                  name="feedConsumption"
                  value={formData.feedConsumption}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">(Given Bag × 50) × 1000 / Live Bird</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Live Bird (Auto)
                </label>
                <input
                  type="number"
                  name="liveBird"
                  value={formData.liveBird}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">Last Live Bird - Mortality</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bird Days (Auto)
                </label>
                <input
                  type="number"
                  name="birdDays"
                  value={formData.birdDays}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">Days from bird date to record date</p>
              </div>
            </div>
          </div>

          {/* Last Record Info */}
          {(lastRecord || selectedFarmerData) && (
            <div className={`p-4 rounded-lg ${recordData ? 'bg-yellow-50 border border-yellow-200' : 'bg-blue-50'}`}>
              <h3 className={`text-lg font-medium mb-2 ${recordData ? 'text-yellow-900' : 'text-blue-900'}`}>
                {recordData ? 'Edit Mode - Reference Information' : 'Reference Information'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                 {lastRecord && (
                   <>
                     <div>
                       <span className="font-medium text-blue-700">Last Tray Stock:</span> {lastRecord.trayStock}
                     </div>
                     <div>
                       <span className="font-medium text-blue-700">Last Bag Stock:</span> {lastRecord.bagStock}
                     </div>
                     <div>
                       <span className="font-medium text-blue-700">Last Bird Days:</span> {lastRecord.birdDays}
                     </div>
                   </>
                 )}
                {selectedFarmerData && (
                  <>
                    <div>
                      <span className="font-medium text-blue-700">Farmer's Live Birds:</span> 
                      <span className={`ml-1 ${selectedFarmerData.liveBirds > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {selectedFarmerData.liveBirds || 0}
                      </span>
                    </div>
                    {selectedFarmerData.birdDate && (
                      <div>
                        <span className="font-medium text-blue-700">Bird Date:</span> {new Date(selectedFarmerData.birdDate).toLocaleDateString()}
                      </div>
                    )}
                  </>
                )}
                {recordData && (
                  <div className="col-span-full mt-2 p-2 bg-yellow-100 rounded text-xs text-yellow-800">
                    <strong>Edit Mode:</strong> Changes to manual fields will recalculate auto-calculated fields based on the differences from the original values.
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : (recordData ? 'Update Record' : 'Add Record')}
            </button>
          </div>
        </form>
      </div>

      {/* Field Comment Modal */}
      {commentModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Add Comment for {commentModal.field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </h3>
              <button
                onClick={closeCommentModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <textarea
              value={commentModal.comment}
              onChange={(e) => setCommentModal(prev => ({ ...prev, comment: e.target.value }))}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              placeholder={`Add a comment for ${commentModal.field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}...`}
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeCommentModal}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={saveFieldComment}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Comment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmRecordsForm; 